import React, { useCallback, useState } from 'react';

import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';

import { zIndex } from 'styles';

import { validateEmail } from '../ContactForm/utils';
import {
  ModalContent,
  EmailInput,
  Button,
  EmailInputContainer,
  FormControlContainer,
} from './ContactModal.style';
import { sendContactForm } from '../../client';

type ContactModalType = 'apply' | 'default';

type ContactModalProps = { onClose: () => void; isOpen: boolean; mode?: ContactModalType };

export const ContactModal = ({ isOpen, onClose, mode = 'default' }: ContactModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [isValid, setIsValid] = useState(true);
  const label = mode === 'default' ? 'Contact us' : 'Apply now';

  const onClick = useCallback(() => {
    if (!validateEmail(email)) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
    setIsLoading(true);

    sendContactForm(email)
      .then(() => {
        onClose();
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsValid(false);
      });
  }, [onClose, email]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmail(e.currentTarget.value);
  }, []);

  return (
    <Modal
      sx={{ zIndex: zIndex.modal }}
      open={isOpen}
      onClose={onClose}
      aria-labelledby="contact-modal-title"
      aria-describedby="contact-modal-description"
    >
      <ModalContent p={[2]}>
        <Typography id="contact-modal-title" variant="h6" component="h2">
          {label}
        </Typography>
        <Typography id="contact-modal-description" sx={{ mt: 2 }}>
          Discover the benefits of synthetic data and simulation
        </Typography>
        <FormControlContainer>
          <EmailInputContainer mt={[0]} mb={[0.75, 0]} mr={[0, 1]}>
            <EmailInput
              error={!isValid}
              disabled={isLoading}
              fullWidth
              value={email}
              placeholder="Email address"
              onChange={onChange}
              aria-describedby="email-error"
              aria-invalid={!isValid}
            />
            {!isValid && (
              <span id="email-error" className="sr-only">
                Please enter a valid email address.
              </span>
            )}
          </EmailInputContainer>
          <Button
            disabled={isLoading}
            size="large"
            onClick={onClick}
            aria-label="Take the first step and subscribe"
          >
            Take the first step
          </Button>
        </FormControlContainer>
      </ModalContent>
    </Modal>
  );
};
